import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { Utils } from 'src/app/core/services/utils';
import { LoanDocDashboardTask } from 'src/app/models/borrower/loan-doc-dashboard-task.model';
import { TaskMessage } from 'src/app/models/message/task-message.model';
import { DocsService } from 'src/app/modules/loan-docs/services/docs.service';
import { FileService } from 'src/app/services/file.service';
import { MessageService } from 'src/app/services/message.service';
import { TaskService } from 'src/app/services/task.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'escalation-task-dialog',
    templateUrl: './escalation-task-dialog.component.html',
    styleUrls: ['./escalation-task-dialog.component.scss'],
    standalone: false
})
export class EscalationTaskDialogComponent implements OnInit {

  @Input()
  task: LoanDocDashboardTask;

  @Input()
  maxSupportedFileSize: number;

  internalTaskMessages: Array<TaskMessage> = [];

  uploadedFiles: Array<File> = [];

  private _autoTransition: boolean = true;
  private _autoConvertToPdf: boolean = false;
  private _useDynamicCompression: boolean = true;

  protected isSaving: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    private readonly _taskService: TaskService,
    private readonly _fileService: FileService,
    private readonly _docsService: DocsService,
    private readonly _messageService: MessageService
  ) { }

  ngOnInit(): void {
    this._messageService
      .getInternalTaskMessages(this.task.loanDocTaskId)
      .subscribe((response) => {
        this.internalTaskMessages = response;
      });
  }

  onFileUploadChanged = (uploadedFiles: File[]) => {
    var validFiles = uploadedFiles;
    var oversizedFiles = [];
    if (this.maxSupportedFileSize != null) {
      oversizedFiles = uploadedFiles.filter(file => file.size > this.maxSupportedFileSize);
      validFiles = uploadedFiles.filter(file => file.size <= this.maxSupportedFileSize);
    }

    const fileNames = oversizedFiles
      .map(file => `• ${file.name} (${Utils.formatBytes(file.size)})`)
      .join('<br>');

    if (oversizedFiles.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'File(s) Too Large',
        html: `The following file(s) exceed the ${Utils.formatBytes(this.maxSupportedFileSize)} limit and will not be uploaded:<br><br>${fileNames}`,
        confirmButtonText: 'OK',
      }).then(() => {
        this.uploadedFiles = validFiles;
      });
      return;
    }
  }

  saveEscalationTask = () => {
    // No need to set this back to false since the modal will be closed.
    this.isSaving = true;

    let docFiles = [];
    this.uploadedFiles.forEach((file) => {
      docFiles.push({
        guid: null,
        file: file,
        fileName: file.name,
        note: '',
        borrowerNote: '',
        active: true,
      });
    });
    this.task.docFiles.concat(docFiles);

    const data = {
      loanDocTask: this.task,
      multipleBorrower: null,
      numFiles: this.task.docFiles.length,
      daysToPostpone: 0,
    };
    // Only move the task into a completed/blocking status AFTER its files upload (LLS-119).
    this._taskService.saveTaskWithFileOps(data, (taskId) => this.buildTaskFileOperations(taskId)).subscribe({
      next: (responseTask) => {
        this.activeModal.close(responseTask);
      },
      error: (err) => {
        console.error('Error saving escalation task', err);
        this.activeModal.close(null);
      }
    });
  };

  private buildTaskFileOperations = (taskId: number): Observable<any>[] => {
    return this.task.docFiles
      .reduce((results, file) => {
        let result: Observable<any>;
        if (file.guid == null) {
          result = this._docsService.uploadFileForTask(
            file.file,
            taskId,
            this._autoTransition,
            this._autoConvertToPdf,
            this._useDynamicCompression
          );
        } else if (file.active === false) {
          result = this._fileService.removeFile(file.guid);
        }

        if (result != null) {
          results.push(result);
        }
        return results;
      }, [] as Observable<any>[]);
  };

  onOkClicked = () => {
    this.saveEscalationTask();
  };

  onCancelClicked = () => {
    this.activeModal.close('cancel');
  };
}
